import { DialogContent, DialogTitle, Dialog as MuiDialog } from '@mui/material';
import dayjs from 'dayjs';
import { default as utc } from 'dayjs/plugin/utc';
import { FC, ReactNode, useMemo, useState } from 'react';
import { Invoice, InvoiceItem } from 'types';

import { Clear } from '@mui/icons-material';
import { EditFreeDays } from './edit-free-days';
import { EditAction } from './enums';
import { EventsView } from './events-view';

dayjs.extend(utc);

type EditInvoiceItemDialogProps = {
  open: boolean;
  close: () => void;
  invoiceItem: InvoiceItem;
  onItemEditSuccess: () => void;
  invoice: Invoice;
};

export const EditInvoiceItemDialog: FC<EditInvoiceItemDialogProps> = ({
  open,
  close,
  invoiceItem,
  onItemEditSuccess,
  invoice
}) => {
  const [selectedAction, setSelectedAction] = useState<EditAction | null>(null);
  const [title, setTitle] = useState('Edit Invoice Item');

  const selectEditActionContent = useMemo<ReactNode>(() => {
    return (
      <div className="d-flex g-3 align-items-center justify-content-center">
        <button
          className="btn-primary"
          onClick={() => {
            setTitle('Edit Free Days');
            setSelectedAction(EditAction.FREE_DAYS);
          }}
        >
          Edit Free Days
        </button>
        <button
          className="btn-primary"
          onClick={() => {
            setTitle('Edit Billable Events');
            setSelectedAction(EditAction.EVENTS);
          }}
        >
          Edit Billable Events
        </button>
      </div>
    );
  }, []);

  const dialogContent = useMemo<ReactNode>(() => {
    if (selectedAction === null) {
      return selectEditActionContent;
    } else if (selectedAction === EditAction.FREE_DAYS) {
      return (
        <EditFreeDays
          invoiceItem={invoiceItem}
          close={close}
          setSelectedAction={setSelectedAction}
          onItemEditSuccess={onItemEditSuccess}
        />
      );
    }
    if (selectedAction === EditAction.EVENTS) {
      return (
        <EventsView
          invoice={invoice}
          invoiceItem={invoiceItem}
          close={close}
          setSelectedAction={setSelectedAction}
          onItemEditSuccess={onItemEditSuccess}
        />
      );
    }
  }, [close, invoice, invoiceItem, onItemEditSuccess, selectEditActionContent, selectedAction]);

  return (
    <MuiDialog
      maxWidth={false}
      open={open}
      sx={{
        '& .MuiPaper-root': {
          background: 'white',
          overflowY: 'unset'
        }
      }}
      onKeyDown={e => {
        if (e.key === 'Escape') {
          e.preventDefault();
          close();
        }
      }}
    >
      <div className="d-flex justify-content-between">
        <DialogTitle>{title}</DialogTitle>
        <div>
          <button className="btn-close mr-3 mt-3" onClick={() => close()}>
            <Clear />
          </button>
        </div>
      </div>
      <DialogContent>{dialogContent}</DialogContent>
    </MuiDialog>
  );
};
